/**
 * @fileoverview A contratação (31/10/2026, etapa 7 dos planos; duas etapas desde
 * 03/11/2026), num modal largo: em "Personalizar assinatura", o plano (Basic ou
 * Pro), as licenças, o Chat Interno e o pacote de IA; em "Revisar e pagar", os
 * dados para a cobrança e a forma de pagamento. O resumo com o total fica sempre
 * à vista, com o botão da próxima ação. No modo "internal", só o Chat Interno no
 * Starter, numa etapa só. No modo "change" (etapa 8), a alteração de quem já
 * assina: parte do que a Empresa tem, só aumenta, mostra a cotação do servidor (o
 * proporcional agora, o que a Empresa paga hoje e a mensalidade nova) e paga no
 * cartão da assinatura, no Pix (o QR Code e o código, acompanhado até vencer) ou
 * no boleto; o valor que a pessoa viu vai junto (se mudou, o servidor recusa e a
 * tela cota de novo). O cartão é tokenizado no navegador com a chave pública que
 * o servidor entrega (o número nunca passa pelo Sttelar). Depois de pagar, o
 * modal acompanha: no cartão, até a confirmação (pago ou recusado); se a Pagar.me
 * não respondeu, o pedido fica em verificação e o modal segue acompanhando (nunca
 * "tente de novo", que criaria outra assinatura); no boleto, espera a linha
 * digitável e o link. O plano só muda quando o pagamento confirma (o servidor
 * decide).
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { CalendarX, Check, Copy, ExternalLink, Repeat, X, Zap } from "lucide-react";
import { toast } from "react-toastify";

import { useBillingProfile } from "../../BillingProfileForm/useBillingProfile";
import PixPayment from "../PixPayment";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { SocketContext } from "../../../context/Socket/SocketContext";
import toastError from "../../../errors/toastError";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { capitalize, contractItems, describeTerms, itemsTotal, money } from "../../../utils/billingPlans";
import { boletoDigits, formatBoletoLine } from "../../../utils/boletoLine";
import { onlyDigits, tokenizeCard } from "../../../utils/cardForm";
import { formatUtcDate } from "../../../utils/formatLocaleDate";
import useCheckoutStyles from "./checkoutStyles";
import CustomizeStep from "./CustomizeStep";
import OrderSummary from "./OrderSummary";
import PaymentStep from "./PaymentStep";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`plansDialog.${key}`, options);

const POLL_MS = 3000;
/** Depois de mudar de etapa, o tempo em que o botão principal não paga (o segundo clique de um clique duplo). */
const PAY_LOCK_MS = 700;
const SLOW_MS = 45000;
const GIVE_UP_MS = 180000;
/** O Pix vale 30 minutos; o servidor ainda espera 10 (o pagamento do último minuto). */
const PIX_MARGIN_MS = 10 * 60 * 1000;
/** Os limites que só o Pro resolve (no Basic, mais licenças não aumentam contas, números nem quadros). */
const PRO_RESOURCES = ["accounts", "whatsapps", "crmBoards"];

const EMPTY_CARD = { number: "", holder: "", expiry: "", cvv: "" };
const CLOSED = ["failed", "expired", "canceled", "in_review"];


/** O que está errado em cada campo do cartão (o vazio também). */
const cardFieldErrors = (card) => {
  const number = onlyDigits(card.number);
  const expiry = onlyDigits(card.expiry);
  const month = Number(expiry.slice(0, 2));
  const year = Number(expiry.slice(2));
  const fullYear = year < 100 ? 2000 + year : year;
  const now = new Date();
  const notExpired = fullYear > now.getFullYear() || (fullYear === now.getFullYear() && month >= now.getMonth() + 1);
  return {
    number: number.length >= 13 && number.length <= 19 ? "" : t("cardErrors.number"),
    holder: card.holder.trim().length >= 2 ? "" : t("cardErrors.holder"),
    expiry: month >= 1 && month <= 12 && expiry.length >= 4 && notExpired ? "" : t("cardErrors.expiry"),
    cvv: /^\d{3,4}$/.test(card.cvv) ? "" : t("cardErrors.cvv"),
  };
};

const PlansDialog = ({ open, onClose, overview, mode = "upgrade", onDone, onProfileSaved, focusResource = null, preferredPlan = null }) => {
  const shared = useStyles();
  const checkout = useCheckoutStyles();
  const classes = { ...shared, ...checkout };
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const socketManager = useContext(SocketContext);
  const { user, impersonation } = useContext(AuthContext);
  const { catalog, usage, publicKey, boletoMinCents } = overview;
  const changeMode = mode === "change";
  // O que a Empresa já tem (a alteração parte daqui e só aumenta).
  const current = {
    plan: overview.plan,
    seats: overview.terms?.seatsContracted ?? 0,
    internalChatSeats: overview.terms?.internalChatSeats ?? 0,
    aiPackMessages: overview.terms?.aiPackMessages ?? null,
  };
  const internalOnly = mode === "internal" || (changeMode && current.plan === "starter");
  const starter = catalog.plans.find((plan) => plan.key === "starter");
  // Na contratação, o plano escolhido no site (o Basic ou o Pro), senão o Basic.
  const initialPlan = changeMode && current.plan !== "starter" ? current.plan : preferredPlan === "pro" ? "pro" : "basic";

  const [plan, setPlan] = useState(initialPlan);
  const definition = catalog.plans.find((item) => item.key === plan) || catalog.plans[1];
  const minSeats = Math.max(definition.minBilledUsers, usage.users, changeMode ? current.seats : 0);
  const [seats, setSeats] = useState(minSeats);
  const minInternal = Math.max(internalOnly && !changeMode ? 1 : 0, usage.internalChatUsers, changeMode ? current.internalChatSeats : 0);
  const maxInternal = internalOnly ? starter.maxUsers : seats;
  const [internalChatSeats, setInternalChatSeats] = useState(minInternal);
  const [aiPackMessages, setAiPackMessages] = useState("");
  const [method, setMethod] = useState("credit_card");
  const [card, setCard] = useState(EMPTY_CARD);
  const [phase, setPhase] = useState("form");
  const [request, setRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [slow, setSlow] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteNonce, setQuoteNonce] = useState(0);
  const [pixExpired, setPixExpired] = useState(false);
  // A contratação só do Chat Interno não tem o que personalizar além do número: fica tudo numa tela.
  const [step, setStep] = useState(internalOnly ? 2 : 1);
  const [editingBilling, setEditingBilling] = useState(false);
  const [cardTouched, setCardTouched] = useState({});
  const [nextCharge, setNextCharge] = useState(null);
  // O servidor recusou por falta de confirmação, ou ela chegou com o modal aberto.
  const [emailRefused, setEmailRefused] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const pollStarted = useRef(0);
  const stateTitle = useRef(null);
  const scrollBox = useRef(null);
  const stepTitle = useRef(null);
  const paymentTitle = useRef(null);
  // A etapa mudou (ou os dados foram salvos) por uma ação: o foco e a rolagem acompanham.
  const moveFocus = useRef(null);
  // O botão principal fica no mesmo lugar nas etapas: o segundo clique de um clique duplo não pode pagar sem a revisão.
  const payLockedUntil = useRef(0);
  const billingNeeded = !changeMode || !overview.profileReady;
  // Quem entra pela equipe da Sttelar não tem o próprio e-mail sugerido como o de cobrança da Empresa.
  const billing = useBillingProfile({ active: open && billingNeeded, prefill: user?.email && !user.super && !impersonation ? { email: user.email } : null });
  // A leitura que falhou com o cadastro existente não abre um formulário em branco por cima dele.
  const billingOpen = billingNeeded && !billing.loading && (editingBilling || (!billing.saved && !overview.profileReady));
  const emailPending = !emailConfirmed && !impersonation && !user?.super && (emailRefused || user?.emailVerified === false);
  const savedCard = overview.subscription?.cardLastFour || null;
  const savedBrand = overview.subscription?.cardBrand || null;
  // O nome do cartão na opção ("Visa final 0010", ou "Cartão final 0010") e dentro das frases ("…no cartão final 0010").
  const savedCardName = savedCard ? (savedBrand ? t("savedCardBrand", { brand: capitalize(savedBrand), last4: savedCard }) : t("savedCard", { last4: savedCard })) : "";
  const savedCardInline = savedCard ? (savedBrand ? t("savedCardBrand", { brand: capitalize(savedBrand), last4: savedCard }) : t("savedCardShort", { last4: savedCard })) : "";

  // Cada abertura começa do zero; vindo do aviso de limite, já com o que resolve (mais uma licença, mais um Chat Interno, o Pro).
  useEffect(() => {
    if (!open) return;
    const startKey = changeMode && current.plan === "basic" && PRO_RESOURCES.includes(focusResource) ? "pro" : initialPlan;
    const startPlan = catalog.plans.find((item) => item.key === startKey) || catalog.plans[1];
    const startSeats = Math.max(startPlan.minBilledUsers, usage.users, changeMode ? current.seats : 0);
    const nextSeats = changeMode && !internalOnly && focusResource === "users" ? Math.min(startSeats + 1, 1000) : startSeats;
    const startInternal = Math.max(internalOnly && !changeMode ? 1 : 0, usage.internalChatUsers, changeMode ? current.internalChatSeats : 0);
    const internalCap = internalOnly ? starter.maxUsers : nextSeats;
    setPlan(startKey);
    setSeats(nextSeats);
    setInternalChatSeats(changeMode && focusResource === "internalChat" ? Math.min(startInternal + 1, internalCap) : startInternal);
    setAiPackMessages(changeMode && current.aiPackMessages ? current.aiPackMessages : "");
    setMethod(changeMode && !savedCard ? "pix" : "credit_card");
    setQuote(null);
    setQuoteError(null);
    setCard(EMPTY_CARD);
    setPhase("form");
    setRequest(null);
    setFormError("");
    setSlow(false);
    setPixExpired(false);
    setStep(internalOnly ? 2 : 1);
    setEditingBilling(false);
    setCardTouched({});
    setNextCharge(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // As licenças nunca abaixo do mínimo do plano escolhido; o Chat Interno, nunca acima delas.
  useEffect(() => {
    if (internalOnly) return;
    setSeats((current) => Math.max(current, minSeats));
  }, [internalOnly, minSeats]);
  useEffect(() => {
    setInternalChatSeats((current) => Math.min(Math.max(current, minInternal), maxInternal));
  }, [minInternal, maxInternal]);

  // Cada fase depois de pagar leva o foco ao título dela (o botão que estava focado sumiu).
  useEffect(() => {
    if (phase !== "form" && stateTitle.current) stateTitle.current.focus();
  }, [phase]);

  useEffect(() => {
    if (!moveFocus.current) return;
    const target = moveFocus.current === "payment" ? paymentTitle.current : stepTitle.current;
    if (moveFocus.current !== "payment" && scrollBox.current) scrollBox.current.scrollTop = 0;
    if (target) target.focus();
    moveFocus.current = null;
  }, [step, billingOpen]);

  // Contratado: a data da próxima cobrança, que só existe depois da assinatura criada.
  useEffect(() => {
    if (phase !== "done" || changeMode) return undefined;
    let alive = true;
    api
      .get("/billing/overview")
      .then(({ data }) => alive && setNextCharge(data.subscription?.nextBillingAt || null))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [phase, changeMode]);

  const terms = internalOnly
    ? { plan: "starter", seats: null, internalChatSeats, aiPackMessages: null }
    : { plan, seats, internalChatSeats, aiPackMessages: aiPackMessages === "" ? null : Number(aiPackMessages) };
  const items = useMemo(() => contractItems(catalog, terms), [catalog, terms.plan, terms.seats, terms.internalChatSeats, terms.aiPackMessages]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = itemsTotal(items);
  // Na alteração, o que se paga agora é o proporcional da cotação do servidor.
  const dueNow = changeMode ? quote?.prorationCents ?? 0 : total;
  const freeChange = changeMode && !!quote && quote.prorationCents < 100;
  // Por que o boleto não serve: abaixo de R$ 50 ou, na alteração, a renovação perto demais (o servidor diz).
  const boletoBlock = changeMode ? (quote ? quote.boletoBlock : "minimum") : total < boletoMinCents ? "minimum" : null;
  const boletoAllowed = !boletoBlock;
  const unchanged =
    changeMode &&
    terms.plan === current.plan &&
    (terms.seats ?? 0) === (current.seats ?? 0) &&
    terms.internalChatSeats === current.internalChatSeats &&
    (terms.aiPackMessages ?? null) === (current.aiPackMessages ?? null);

  // A cotação da alteração, a cada mudança (o servidor calcula o proporcional pelo ciclo da assinatura).
  // Igual ao contrato, nem pergunta; enquanto calcula, o valor anterior fica esmaecido.
  const termsKey = JSON.stringify(terms);
  useEffect(() => {
    if (!open || !changeMode || phase !== "form") return undefined;
    if (unchanged) {
      setQuote(null);
      setQuoteError("ERR_BILLING_NOTHING_TO_CHANGE");
      setQuoting(false);
      return undefined;
    }
    let alive = true;
    setQuoting(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/billing/changes/quote", JSON.parse(termsKey));
        if (alive) {
          setQuote(data.quote);
          setQuoteError(null);
        }
      } catch (err) {
        if (alive) {
          setQuote(null);
          setQuoteError(err?.response?.data?.error || "network");
        }
      } finally {
        if (alive) setQuoting(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [open, changeMode, phase, termsKey, unchanged, quoteNonce]);

  // O boleto que deixou de servir: na alteração, o Pix (nunca o cartão sem a pessoa escolher); na contratação, o cartão.
  useEffect(() => {
    if (method !== "boleto" || boletoAllowed) return;
    if (!changeMode) setMethod("credit_card");
    else if (quote && !quoting) setMethod("pix");
  }, [boletoAllowed, method, changeMode, quote, quoting]);

  const startPolling = () => {
    pollStarted.current = Date.now();
    setSlow(false);
  };

  const showRequest = (next) => {
    setRequest(next);
    if (next.status === "applied") setPhase("done");
    else if (CLOSED.includes(next.status)) setPhase("failed");
    else if (next.status === "awaiting_payment" && next.paymentMethod === "pix") {
      if (phase !== "pix") startPolling();
      setPhase("pix");
    } else if (next.status === "awaiting_payment" && next.paymentMethod === "boleto") {
      if (phase !== "boleto") startPolling();
      setPhase("boleto");
    } else {
      // Aguardando o cartão, ou em verificação (a Pagar.me não respondeu a tempo).
      if (phase !== "paying") startPolling();
      setPhase("paying");
    }
  };

  // Acompanha o pedido: no cartão, até a confirmação; no Pix, até ele vencer (mais a margem do servidor), sem o
  // "está demorando" (abrir o app do banco leva tempo); no boleto, até chegar a linha digitável.
  const waitingBoletoLine = phase === "boleto" && request && !request.boleto?.line;
  const pixExpiresAt = request?.pix?.expiresAt ? new Date(request.pix.expiresAt).getTime() : null;
  useEffect(() => {
    if (!request || !(phase === "paying" || phase === "pix" || waitingBoletoLine)) return undefined;
    let alive = true;
    const timer = setInterval(async () => {
      const elapsed = Date.now() - pollStarted.current;
      if (phase === "pix") {
        if (pixExpiresAt && Date.now() > pixExpiresAt + PIX_MARGIN_MS) {
          clearInterval(timer);
          return;
        }
      } else {
        if (elapsed > SLOW_MS) setSlow(true);
        if (elapsed > GIVE_UP_MS) {
          clearInterval(timer);
          return;
        }
      }
      try {
        const { data } = await api.get(`/billing/checkout/${request.id}`);
        if (alive) showRequest(data.request);
      } catch (err) {
        // Uma leitura que falha não encerra o acompanhamento.
      }
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, request?.id, waitingBoletoLine, pixExpiresAt]);

  // O Pix vencido: a tela diz e oferece outro (o servidor ainda segura o pedido pela margem).
  useEffect(() => {
    if (phase !== "pix" || !pixExpiresAt) return undefined;
    const wait = pixExpiresAt - Date.now();
    if (wait <= 0) {
      setPixExpired(true);
      return undefined;
    }
    const timer = setTimeout(() => setPixExpired(true), wait);
    return () => clearTimeout(timer);
  }, [phase, pixExpiresAt]);

  // O aviso do servidor (o pagamento confirmou, o pedido mudou): confere na hora, sem esperar a próxima leitura.
  useEffect(() => {
    if (!request?.id || !["paying", "pix", "boleto"].includes(phase) || !socketManager) return undefined;
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);
    // A resposta que chega depois de a fase ou o pedido mudarem (o aviso da própria desistência, antes de
    // "Gerar outro Pix") não mexe na tela: como no acompanhamento, só vale enquanto o efeito está vivo.
    let alive = true;
    const onBilling = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${request.id}`);
        if (alive) showRequest(data.request);
      } catch (err) {
        // A leitura seguinte confere.
      }
    };
    socket.on(`company-${companyId}-billing`, onBilling);
    return () => {
      alive = false;
      socket.off(`company-${companyId}-billing`, onBilling);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketManager, request?.id, phase]);

  const submit = async () => {
    if (Date.now() < payLockedUntil.current) return;
    setFormError("");
    if (changeMode) {
      setSubmitting(true);
      try {
        // O valor que a pessoa viu vai junto: se mudou (o ciclo virou, outra aba), o servidor recusa e a tela cota de novo.
        const { data } = await api.post("/billing/changes", { ...terms, paymentMethod: method, expectedProrationCents: quote?.prorationCents });
        showRequest(data.request);
      } catch (err) {
        const code = err?.response?.data?.error;
        if (code === "ERR_BILLING_CARD_REJECTED") {
          setRequest({ kind: "change", paymentMethod: "credit_card", status: "failed" });
          setPhase("failed");
        } else if (code === "ERR_BILLING_QUOTE_CHANGED") {
          setFormError(t("quoteChanged"));
          setQuoteNonce((value) => value + 1);
        } else if (code === "ERR_BILLING_REQUEST_OPEN") {
          toast.info(t("openRequest"));
          onDone();
        } else if (code === "ERR_EMAIL_NOT_VERIFIED") {
          setEmailConfirmed(false);
          setEmailRefused(true);
        } else {
          toastError(err);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (method === "credit_card") {
      const wrong = ["number", "holder", "expiry", "cvv"].find((key) => cardFieldErrors(card)[key]);
      if (wrong) {
        setCardTouched({ number: true, holder: true, expiry: true, cvv: true });
        const node = document.getElementById(`plans-card-${wrong}`);
        if (node) node.focus();
        return;
      }
    }
    setSubmitting(true);
    try {
      let cardToken;
      if (method === "credit_card") {
        try {
          cardToken = await tokenizeCard(card, publicKey);
        } catch (err) {
          // 400/422: algum dado do cartão; o resto é a operadora (domínio, rede), e nada foi cobrado.
          setFormError(err.status === 400 || err.status === 422 ? t("cardInvalid") : t("cardServiceDown"));
          return;
        }
      }
      const { data } = await api.post("/billing/checkout", { ...terms, paymentMethod: method, cardToken });
      showRequest(data.request);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_BILLING_CARD_REJECTED") {
        setRequest({ paymentMethod: "credit_card", status: "failed" });
        setPhase("failed");
      } else if (code === "ERR_BILLING_REQUEST_OPEN") {
        toast.info(t("openRequest"));
        onDone();
      } else if (code === "ERR_EMAIL_NOT_VERIFIED") {
        setEmailConfirmed(false);
        setEmailRefused(true);
      } else {
        toastError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (next) => {
    setFormError("");
    payLockedUntil.current = Date.now() + PAY_LOCK_MS;
    moveFocus.current = "step";
    setStep(next);
  };

  // Salvos os dados, a tela segue para o pagamento, com as escolhas e o que já foi digitado no cartão.
  const saveBilling = async () => {
    setFormError("");
    moveFocus.current = "payment";
    const result = await billing.save();
    if (!result.ok) {
      moveFocus.current = null;
      if (result.code === "ERR_EMAIL_NOT_VERIFIED") {
        setEmailConfirmed(false);
        setEmailRefused(true);
      } else if (!result.handled) {
        setFormError(result.code && i18n.exists(`backendErrors.${result.code}`) ? i18n.t(`backendErrors.${result.code}`) : t("billingSaveFailed"));
      }
      return;
    }
    payLockedUntil.current = Date.now() + PAY_LOCK_MS;
    setEditingBilling(false);
    if (onProfileSaved) onProfileSaved();
  };

  const cancelEditBilling = () => {
    billing.discard();
    setEditingBilling(false);
  };

  const emailWasConfirmed = useCallback(() => {
    setEmailRefused(false);
    setEmailConfirmed(true);
  }, []);

  // Tentar de novo: na alteração, o cartão salvo recusado não se repete (vai para o Pix).
  const retry = () => {
    if (changeMode && method === "credit_card") setMethod("pix");
    setCard((current) => ({ ...current, cvv: "" }));
    setCardTouched((current) => ({ ...current, cvv: false }));
    setRequest(null);
    setPixExpired(false);
    setPhase("form");
  };

  // Outro Pix: o vencido é desistido antes (o servidor segura o pedido pela margem e só deixa um aberto).
  const pixAgain = async () => {
    setSubmitting(true);
    try {
      await api.post(`/billing/checkout/${request.id}/cancel`);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_BILLING_ALREADY_PAID") {
        const { data } = await api.get(`/billing/checkout/${request.id}`).catch(() => ({ data: null }));
        if (data?.request) showRequest(data.request);
        setSubmitting(false);
        return;
      }
      if (code !== "ERR_BILLING_REQUEST_CLOSED") {
        toastError(err);
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(false);
    setMethod("pix");
    setRequest(null);
    setPixExpired(false);
    setQuoteNonce((value) => value + 1);
    setPhase("form");
  };

  const copyLine = async (line) => {
    try {
      await navigator.clipboard.writeText(boletoDigits(line));
      toast.success(i18n.t("billingOverview.pending.copied"));
    } catch (err) {
      toast.info(boletoDigits(line));
    }
  };

  // Com um pedido criado, fechar sempre relê Faturamento (ele aparece lá, pago ou aguardando, com o Pix ou o boleto).
  const finish = () => (request?.id ? onDone() : onClose());
  // O clique fora (ao pegar o celular para pagar) não fecha o Pix nem o boleto, nem o formulário de pagamento (perderia o
  // que foi digitado): só o X, o Esc ou o "Fechar".
  const closeDialog = (event, reason) => {
    if (submitting || billing.saving) return;
    if (reason === "backdropClick" && (phase === "pix" || phase === "boleto" || (phase === "form" && step === 2))) return;
    finish();
  };

  const paidPlan = catalog.plans.find((item) => item.key === (request?.changes?.plan || terms.plan));

  const allCardErrors = cardFieldErrors(card);
  // O erro de um campo só aparece depois de a pessoa sair dele (ou tentar pagar).
  const cardErrors = Object.keys(allCardErrors).reduce((errors, key) => ({ ...errors, [key]: cardTouched[key] ? allCardErrors[key] : "" }), {});

  // A ação principal, sempre no mesmo lugar: a próxima coisa a fazer.
  const primary = (() => {
    if (step === 2 && emailPending) return { label: t("confirmEmail.cta"), disabled: true };
    if (step === 2 && billingOpen) return { label: t("saveBilling"), disabled: billing.saving || billing.zipLoading, busy: billing.saving, onClick: saveBilling };
    if (changeMode && !quote) {
      const label = quoting ? t("quoting") : !quoteError || quoteError === "ERR_BILLING_NOTHING_TO_CHANGE" ? t("quoteNothing") : step === 1 ? t("continue") : t("submitDisabled");
      return { label, disabled: true };
    }
    if (step === 1) return { label: freeChange ? t("continueFree") : t("continue"), disabled: !items.length || (changeMode && quoting), onClick: () => goToStep(2) };
    if (billingNeeded && billing.loading) return { label: t("submitDisabled"), disabled: true };
    const label = freeChange
      ? t("submitFree")
      : method === "pix"
        ? t("submitPix", { amount: money(dueNow) })
        : method === "credit_card"
          ? t("submitCard", { amount: money(dueNow) })
          : t("submitBoleto", { amount: money(dueNow) });
    return { label, disabled: submitting || !items.length || (changeMode && (!quote || quoting)), busy: submitting, onClick: submit };
  })();

  // Onde cai a cobrança, colado ao botão. Na barra fixa, a frase da etapa 1 ocuparia o espaço das escolhas.
  const ctaNote =
    step === 1
      ? compact
        ? null
        : t("continueNote")
      : changeMode && quote && !freeChange
        ? method === "credit_card"
          ? t("chargeCard", { card: savedCardInline })
          : method === "pix"
            ? t("chargePix")
            : t("chargeBoleto")
        : null;

  // As condições reais da cobrança: como se repete, quando vale e como cancelar.
  const trustNotes = (() => {
    // Na alteração sem cobrança, a própria etapa explica: nada de "assim que o pagamento for confirmado".
    if (step === 1 || freeChange) return [];
    if (changeMode) {
      return [
        { icon: Repeat, text: t("subtitleChange") },
        { icon: Zap, text: method === "pix" ? t("trust.changePix") : method === "boleto" ? t("trust.changeBoleto") : t("trust.changeCard") },
      ];
    }
    const boleto = method === "boleto";
    return [
      { icon: Repeat, text: boleto ? t("trust.recurringBoleto") : t("trust.recurringCard") },
      { icon: Zap, text: boleto ? t("trust.activationBoleto") : internalOnly ? t("trust.activationInternal") : t("trust.activationCard") },
      { icon: CalendarX, text: t("trust.cancel") },
    ];
  })();

  const renderForm = () => (
    <div className={classes.body}>
      <div className={classes.main} ref={scrollBox}>
        {step === 1 ? (
          <CustomizeStep
            classes={classes}
            titleRef={stepTitle}
            catalog={catalog}
            changeMode={changeMode}
            current={current}
            plan={plan}
            onPlanChange={setPlan}
            definition={definition}
            seats={seats}
            minSeats={minSeats}
            onSeatsChange={setSeats}
            internalChatSeats={internalChatSeats}
            minInternal={minInternal}
            maxInternal={maxInternal}
            onInternalChange={setInternalChatSeats}
            aiPackMessages={aiPackMessages}
            onAiPackChange={setAiPackMessages}
          />
        ) : (
          <PaymentStep
            classes={classes}
            titleRef={stepTitle}
            paymentTitleRef={paymentTitle}
            internalOnly={internalOnly}
            catalog={catalog}
            internalHelp={t("internalStarterHelp", { max: starter.maxUsers })}
            internalChatSeats={internalChatSeats}
            minInternal={minInternal}
            maxInternal={maxInternal}
            onInternalChange={setInternalChatSeats}
            onBack={() => goToStep(1)}
            emailPending={emailPending}
            email={user?.email}
            onEmailConfirmed={emailWasConfirmed}
            showBilling={billingNeeded}
            billing={billing}
            billingOpen={billingOpen}
            onEditBilling={() => setEditingBilling(true)}
            onCancelEditBilling={cancelEditBilling}
            showPayment={!billingOpen && !freeChange && !(billingNeeded && billing.loading)}
            freeChange={freeChange}
            paymentProps={{
              changeMode,
              savedCardName,
              method,
              onMethodChange: setMethod,
              boletoAllowed,
              boletoBlock,
              boletoMinCents,
              quote,
              card,
              onCardChange: setCard,
              cardErrors,
              onCardBlur: (key) => setCardTouched((touched) => (touched[key] ? touched : { ...touched, [key]: true })),
            }}
            compact={compact}
            notes={trustNotes}
          />
        )}
      </div>
      <OrderSummary
        classes={classes}
        compact={compact}
        items={items}
        total={total}
        changeMode={changeMode}
        quote={quote}
        quoting={quoting}
        quoteError={quoteError}
        currentMonthlyCents={overview.subscription ? overview.subscription.monthlyCents : null}
        dueNow={dueNow}
        freeChange={freeChange}
        onRetryQuote={() => setQuoteNonce((value) => value + 1)}
        primary={primary}
        note={ctaNote}
        formError={formError}
        notes={trustNotes}
      />
    </div>
  );

  const titleProps = { className: classes.stateTitle, ref: stateTitle, tabIndex: -1 };

  const renderState = () => {
    if (phase === "paying") {
      const verifying = request?.status === "creating";
      const pixPending = changeMode && request?.paymentMethod === "pix";
      return (
        <div className={classes.state} role="status">
          <CircularProgress size={34} color="inherit" />
          <h3 {...titleProps}>{pixPending ? t("pixGenerating") : verifying ? t("verifying") : t("paying")}</h3>
          <p className={classes.stateText}>
            {pixPending
              ? slow
                ? t("pixVerifyingSlow")
                : t("pixVerifyingText")
              : slow
                ? t("payingSlow")
                : verifying
                  ? changeMode
                    ? t("verifyingChange")
                    : t("verifyingText")
                  : t("payingText")}
          </p>
          {slow && (
            <div className={classes.stateActions}>
              <button type="button" className={classes.secondary} onClick={onDone}>
                {t("close")}
              </button>
            </div>
          )}
        </div>
      );
    }
    if (phase === "pix" && pixExpired) {
      return (
        <div className={classes.state} role="alert">
          <span className={`${classes.stateIcon} ${classes.stateBad}`}>
            <X />
          </span>
          <h3 {...titleProps}>{t("pixExpiredTitle")}</h3>
          <p className={classes.stateText}>{t("pixExpiredText")}</p>
          <div className={classes.stateActions}>
            <button type="button" className={classes.secondary} onClick={finish} disabled={submitting}>
              {t("close")}
            </button>
            <button type="button" className={`${classes.primary} ${classes.primaryInline}`} onClick={pixAgain} disabled={submitting}>
              {submitting && <CircularProgress size={16} color="inherit" />}
              {t("pixAgain")}
            </button>
          </div>
        </div>
      );
    }
    if (phase === "pix") {
      return (
        <div className={classes.state}>
          <h3 {...titleProps}>{t("pixTitle")}</h3>
          <p className={classes.stateText}>{t("pixText")}</p>
          <PixPayment pix={request?.pix} amountCents={request?.prorationCents ?? 0} />
          <div className={classes.stateActions}>
            <button type="button" className={classes.secondary} onClick={finish}>
              {t("close")}
            </button>
          </div>
          <p className={classes.stateHint}>{t("pixCloseHint")}</p>
        </div>
      );
    }
    if (phase === "done") {
      const internal = request?.changes?.plan === "starter";
      return (
        <div className={classes.state} role="status">
          <span className={`${classes.stateIcon} ${classes.stateOk}`}>
            <Check />
          </span>
          <h3 {...titleProps}>{request?.kind === "change" && (request.prorationCents ?? 0) < 100 ? t("doneChangeTitle") : t("doneTitle")}</h3>
          <p className={classes.stateText}>
            {request?.kind === "change"
              ? t("doneChange", { what: describeTerms(catalog, request.changes) })
              : internal
                ? t("doneInternal", { count: request.changes.internalChatSeats })
                : t("donePlan", { plan: paidPlan?.name })}
          </p>
          {(changeMode ? quote?.nextBillingAt : nextCharge) && (
            <p className={classes.stateHint}>{t("doneNext", { date: formatUtcDate(changeMode ? quote.nextBillingAt : nextCharge) })}</p>
          )}
          <div className={classes.stateActions}>
            <button type="button" className={`${classes.primary} ${classes.primaryInline}`} onClick={onDone}>
              {t("close")}
            </button>
          </div>
        </div>
      );
    }
    if (phase === "failed") {
      const paymentMethod = request?.paymentMethod;
      // Pago fora do prazo (ou com a Empresa já mudada): a equipe confere; pagar de novo não é o caminho.
      const review = request?.status === "in_review";
      const pixExpiredNow =
        paymentMethod === "pix" &&
        (request?.status === "expired" || (request?.status === "failed" && !!request?.pix?.expiresAt && new Date(request.pix.expiresAt).getTime() <= Date.now()));
      const title = review
        ? t("reviewTitle")
        : pixExpiredNow
          ? t("pixExpiredTitle")
          : paymentMethod === "boleto"
            ? t("failedBoletoTitle")
            : paymentMethod === "pix"
              ? t("failedPixTitle")
              : t("failedTitle");
      const text = review
        ? t("reviewText")
        : pixExpiredNow
          ? t("pixExpiredText")
          : paymentMethod === "boleto"
            ? changeMode
              ? t("failedBoletoChange")
              : t("failedBoleto")
            : paymentMethod === "pix"
              ? t("failedPix")
              : changeMode && savedCardInline
                ? t("failedSavedCard", { card: savedCardInline })
                : t("failedCard");
      return (
        <div className={classes.state} role="alert">
          <span className={`${classes.stateIcon} ${classes.stateBad}`}>
            <X />
          </span>
          <h3 {...titleProps}>{title}</h3>
          <p className={classes.stateText}>{text}</p>
          <div className={classes.stateActions}>
            <button type="button" className={review ? `${classes.primary} ${classes.primaryInline}` : classes.secondary} onClick={finish}>
              {t("close")}
            </button>
            {!review && (
              <button type="button" className={`${classes.primary} ${classes.primaryInline}`} onClick={retry}>
                {pixExpiredNow ? t("pixAgain") : changeMode && paymentMethod === "credit_card" ? t("payOtherWay") : t("retry")}
              </button>
            )}
          </div>
        </div>
      );
    }
    const boleto = request?.boleto;
    return (
      <div className={classes.state} role="status">
        {!boleto?.line && <CircularProgress size={28} color="inherit" />}
        <h3 {...titleProps}>{boleto?.line ? t("boletoTitle") : t("boletoGenerating")}</h3>
        <p className={classes.stateText}>{boleto?.dueAt ? t("boletoText", { date: formatUtcDate(boleto.dueAt) }) : t("boletoTextNoDate")}</p>
        {boleto?.line && (
          <div className={classes.boletoLine}>
            <span className={classes.boletoDigits}>{formatBoletoLine(boleto.line)}</span>
            <button type="button" className={classes.secondary} onClick={() => copyLine(boleto.line)}>
              <Copy />
              {i18n.t("billingOverview.pending.copy")}
            </button>
          </div>
        )}
        <div className={classes.stateActions}>
          {boleto?.url && (
            <a className={classes.secondary} href={boleto.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink />
              {i18n.t("billingOverview.pending.open")}
            </a>
          )}
          <button type="button" className={`${classes.primary} ${classes.primaryInline}`} onClick={onDone}>
            {t("close")}
          </button>
        </div>
      </div>
    );
  };

  const inForm = phase === "form";
  const title = changeMode ? t("titleChange") : internalOnly ? t("titleInternal") : t("title");

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth={false}
      classes={{ paper: inForm ? classes.paper : classes.paperState }}
      aria-labelledby="plans-dialog-title"
    >
      <div className={classes.head}>
        <h2 className={classes.headTitle} id="plans-dialog-title">
          {title}
        </h2>
        {inForm && !internalOnly && (
          <ol className={classes.steps} aria-label={t("stepsLabel")}>
            <li>
              {step === 2 ? (
                <button type="button" className={`${classes.stepItem} ${classes.stepBack}`} onClick={() => goToStep(1)} aria-label={t("stepCustomize")}>
                  <span className={`${classes.stepDot} ${classes.stepDotOn}`}>
                    <Check aria-hidden="true" strokeWidth={3} />
                  </span>
                  <span className={classes.stepText}>{t("stepCustomize")}</span>
                </button>
              ) : (
                <span className={`${classes.stepItem} ${classes.stepCurrent}`} aria-current="step">
                  <span className={`${classes.stepDot} ${classes.stepDotOn}`}>1</span>
                  <span className={classes.stepTextCurrent}>{t("stepCustomize")}</span>
                </span>
              )}
            </li>
            <li className={classes.stepLine} aria-hidden="true" />
            <li>
              <span className={`${classes.stepItem} ${step === 2 ? classes.stepCurrent : ""}`} aria-current={step === 2 ? "step" : undefined}>
                <span className={`${classes.stepDot} ${step === 2 ? classes.stepDotOn : ""}`}>2</span>
                <span className={step === 2 ? classes.stepTextCurrent : classes.stepText}>{t("stepPayment")}</span>
              </span>
            </li>
          </ol>
        )}
        <IconButton size="small" className={classes.headClose} onClick={finish} disabled={submitting || billing.saving} aria-label={t("close")}>
          <X size={18} />
        </IconButton>
      </div>
      {inForm ? renderForm() : <div className={classes.stateScroll}>{renderState()}</div>}
    </Dialog>
  );
};

export default PlansDialog;
